// GIWAHS content + SEO data
// FICTIONAL PLACEHOLDER content used for MVP demonstration only.

export const GUIDE_CATEGORIES = [
  { slug: 'all', name: 'All Guides' },
  { slug: 'sourcing', name: 'Sourcing' },
  { slug: 'manufacturing', name: 'Manufacturing' },
  { slug: 'compliance', name: 'Compliance' },
  { slug: 'country', name: 'Country' },
  { slug: 'category', name: 'Category' },
];

export const GUIDES = [
  {
    slug: 'source-streetwear-portugal',
    title: 'How to Source Streetwear in Portugal',
    category: 'country',
    categoryLabel: 'Country',
    readingMin: 9,
    summary: 'Why Portugal punches above its weight for premium streetwear — capacity, fabric mills, MOQ realities, and the hidden cost of "Made in Europe".',
    author: 'Editorial Desk',
    cover: 'https://images.unsplash.com/photo-1603189343302-e603f7add05a?crop=entropy&cs=srgb&fm=jpg&q=85',
    related: { categorySlug: 'streetwear-manufacturers', country: 'Portugal', supplierSlug: 'northgate-apparel-collective' },
  },
  {
    slug: 'moq-negotiation-101',
    title: 'MOQ Negotiation 101',
    category: 'sourcing',
    categoryLabel: 'Sourcing',
    readingMin: 6,
    summary: 'A buyer\'s playbook — when factories will flex below stated MOQ, what to offer in exchange, and the four levers that actually move the number.',
    author: 'Editorial Desk',
    cover: 'https://images.pexels.com/photos/18541463/pexels-photo-18541463.jpeg',
    related: { categorySlug: null, country: null, supplierSlug: null },
  },
  {
    slug: 'understanding-smeta-audits',
    title: 'Understanding SMETA Audits',
    category: 'compliance',
    categoryLabel: 'Compliance',
    readingMin: 11,
    summary: 'What a SMETA audit actually proves, what it doesn\'t, and the seven sections of the report buyers should focus on before signing.',
    author: 'Editorial Desk',
    cover: 'https://images.pexels.com/photos/4614227/pexels-photo-4614227.jpeg',
    related: { categorySlug: null, country: null, supplierSlug: 'dhaka-loomline-textiles' },
  },
  {
    slug: 'oem-vs-odm',
    title: 'OEM vs ODM, Explained',
    category: 'manufacturing',
    categoryLabel: 'Manufacturing',
    readingMin: 7,
    summary: 'Direct Manufacturer, OEM, ODM, Trading Company — what each classification actually means for your margin, lead time, and design control.',
    author: 'Editorial Desk',
    cover: 'https://static.prod-images.emergentagent.com/jobs/78d6f77c-7a7f-48ae-b71b-ec8e8809cfb7/images/4ad6059937baee7d67f0535497c968eccfe7fc143d9d31462a48e40d95fb3cda.png',
    related: { categorySlug: null, country: null, supplierSlug: null },
  },
  {
    slug: 'best-countries-activewear',
    title: 'Best Countries for Activewear Manufacturing',
    category: 'category',
    categoryLabel: 'Category',
    readingMin: 10,
    summary: 'A comparative intelligence brief — China (seamless), Vietnam (cut-and-sew at scale), Turkey (premium knits), Portugal (small batch). MOQ, lead time, cost.',
    author: 'Editorial Desk',
    cover: 'https://images.unsplash.com/photo-1517649763962-0c623066013b?auto=format&fit=crop&q=85',
    related: { categorySlug: 'activewear-factories', country: null, supplierSlug: 'shenzhen-vector-mfg' },
  },
  {
    slug: 'identify-legit-factories',
    title: 'How to Identify Legit Factories',
    category: 'sourcing',
    categoryLabel: 'Sourcing',
    readingMin: 8,
    summary: 'Twelve red flags, four green flags, and the three documents every buyer should demand before sampling — without scaring off the good ones.',
    author: 'Editorial Desk',
    cover: 'https://static.prod-images.emergentagent.com/jobs/78d6f77c-7a7f-48ae-b71b-ec8e8809cfb7/images/c6786a879af9069bce5e7ce80c554929535451cbd28c7da0f3ca7036d7dd8390.png',
    related: { categorySlug: null, country: null, supplierSlug: null },
  },
];

// SEO Category Landing Page configs (drop-in slugs for /category/:slug)
export const CATEGORY_LANDINGS = {
  'streetwear-manufacturers': {
    slug: 'streetwear-manufacturers',
    h1: 'Streetwear Manufacturers',
    title: 'Streetwear Manufacturers — Premium Cut-and-Sew Factories Worldwide',
    intro: 'Editorial directory of verified streetwear factories — heavyweight fleece, garment-dyed knits, premium graphics, and elevated cut-and-sew. Indexed across Portugal, Turkey, China, and India.',
    productCategorySlugs: ['streetwear', 'outerwear'],
    relatedGuides: ['source-streetwear-portugal', 'oem-vs-odm', 'moq-negotiation-101'],
    seoNotes: 'High-intent buyer phrase. Targets brands sourcing premium streetwear.',
  },
  'cap-manufacturers': {
    slug: 'cap-manufacturers',
    h1: 'Cap & Headwear Manufacturers',
    title: 'Cap & Headwear Manufacturers — 5-Panel, Bucket, Beanie Factories',
    intro: 'Specialised headwear factories — 5-panel caps, bucket hats, beanies, structured snapbacks. Custom closures, embroidery, and woven labels.',
    productCategorySlugs: ['caps-headwear', 'accessories'],
    relatedGuides: ['moq-negotiation-101', 'identify-legit-factories'],
    seoNotes: 'Niche but high-volume search. Streetwear brands routinely look for cap factories separately.',
  },
  'activewear-factories': {
    slug: 'activewear-factories',
    h1: 'Activewear Factories',
    title: 'Activewear Factories — Performance Knit & Seamless Manufacturers',
    intro: 'Seamless knitting, sublimation print, moisture-wicking fabrics, compression. The activewear category demands technical capability — these factories deliver.',
    productCategorySlugs: ['activewear'],
    relatedGuides: ['best-countries-activewear', 'oem-vs-odm'],
    seoNotes: 'Fast-growing category with strong commercial intent.',
  },
  'workwear-suppliers': {
    slug: 'workwear-suppliers',
    h1: 'Workwear Suppliers',
    title: 'Workwear Suppliers — Heavyweight Twill, Canvas, Denim Factories',
    intro: 'Heavy-duty workwear factories — 12-14oz canvas, reinforced stitching, cargo construction, selvedge denim. Industrial heritage suppliers.',
    productCategorySlugs: ['workwear', 'denim'],
    relatedGuides: ['identify-legit-factories', 'moq-negotiation-101', 'understanding-smeta-audits'],
    seoNotes: 'Reviving category — workwear-as-fashion driving fresh demand.',
  },
  'denim-manufacturers': {
    slug: 'denim-manufacturers',
    h1: 'Denim Manufacturers',
    title: 'Denim Manufacturers — Selvedge, Wash & Finish Factories',
    intro: 'Boutique to scale denim factories with full vertical capability — selvedge weaving, laser whiskering, hand distressing, sustainable wash houses.',
    productCategorySlugs: ['denim'],
    relatedGuides: ['oem-vs-odm', 'understanding-smeta-audits'],
    seoNotes: 'Premium category, strong margin opportunity.',
  },
  'footwear-factories': {
    slug: 'footwear-factories',
    h1: 'Footwear Factories',
    title: 'Footwear Factories — Vulcanized, Cup Sole, Leather Upper',
    intro: 'Specialist footwear factories — vulcanized soles, cup-sole construction, leather uppers, custom lasts. High-quality cut-and-stitch capacity.',
    productCategorySlugs: ['footwear'],
    relatedGuides: ['identify-legit-factories', 'oem-vs-odm'],
    seoNotes: 'Highly specialised supply chain — buyers prefer dedicated index.',
  },
};

// SEO Country Sourcing Page configs (drop-in for /manufacturers/:country slug)
export const COUNTRY_LANDINGS = {
  china: {
    slug: 'china',
    name: 'China',
    h1: 'Manufacturers in China',
    title: 'Source from China — Verified Factories Directory',
    intro: 'The world\'s deepest manufacturing base for fashion, footwear, and accessories. Indexed across Guangdong, Zhejiang, Fujian and Shanghai with full verification.',
    strengths: [
      { cat: 'Activewear', note: 'Seamless knit, sublimation, performance fabrics.' },
      { cat: 'Footwear', note: 'Vulcanized + cup-sole, deep leather supply.' },
      { cat: 'Streetwear', note: 'Premium fleece, embroidery, embellishment.' },
      { cat: 'Accessories', note: 'Bags, headwear, technical hardware.' },
    ],
    moqTrend: 'MEDIUM-HIGH · 500–5,000 typical for verified factories',
    leadTimeTrend: '30–60 days',
    commonCerts: ['ISO 9001', 'BSCI', 'OEKO-TEX', 'GRS'],
    relatedGuides: ['oem-vs-odm', 'best-countries-activewear', 'identify-legit-factories'],
  },
  vietnam: {
    slug: 'vietnam',
    name: 'Vietnam',
    h1: 'Manufacturers in Vietnam',
    title: 'Source from Vietnam — Verified Factories Directory',
    intro: 'The leading alternative to China for cut-and-sew at scale — sportswear, casualwear, and headwear factories with strong vertical capability.',
    strengths: [
      { cat: 'Caps & Headwear', note: 'Embroidery, structured + unstructured construction.' },
      { cat: 'Activewear', note: 'Cut-and-sew capacity for major sports brands.' },
      { cat: 'Outerwear', note: 'Down, technical shells, packable construction.' },
    ],
    moqTrend: 'MEDIUM · 200–3,000 typical',
    leadTimeTrend: '25–45 days',
    commonCerts: ['BSCI', 'WRAP', 'OEKO-TEX'],
    relatedGuides: ['oem-vs-odm', 'moq-negotiation-101'],
  },
  bangladesh: {
    slug: 'bangladesh',
    name: 'Bangladesh',
    h1: 'Manufacturers in Bangladesh',
    title: 'Source from Bangladesh — Verified Factories Directory',
    intro: 'High-volume cut-and-sew and woven specialisation — denim, workwear, and basic knits at scale, with maturing sustainability practices.',
    strengths: [
      { cat: 'Denim', note: 'Industrial-scale wash and finish capability.' },
      { cat: 'Workwear', note: 'Heavy-duty canvas + twill at volume.' },
      { cat: 'Streetwear', note: 'Cost-leading cut-and-sew for basics.' },
    ],
    moqTrend: 'HIGH · 1,000–10,000+ typical',
    leadTimeTrend: '40–70 days',
    commonCerts: ['WRAP', 'BSCI', 'Sedex SMETA', 'ISO 14001'],
    relatedGuides: ['understanding-smeta-audits', 'moq-negotiation-101'],
  },
  india: {
    slug: 'india',
    name: 'India',
    h1: 'Manufacturers in India',
    title: 'Source from India — Verified Factories Directory',
    intro: 'Global leader in organic cotton manufacturing, with strong reactive dyeing and tubular knit capability. Tirupur is the densest specialisation hub.',
    strengths: [
      { cat: 'Streetwear', note: 'Organic cotton basics, reactive dye.' },
      { cat: 'Activewear', note: 'Cotton-spandex blends, fitness essentials.' },
      { cat: 'Accessories', note: 'Tote bags, soft accessories, scarves.' },
    ],
    moqTrend: 'MEDIUM-HIGH · 500–5,000 typical',
    leadTimeTrend: '35–55 days',
    commonCerts: ['GOTS', 'Fair Trade', 'OEKO-TEX', 'GRS', 'ISO 14001'],
    relatedGuides: ['understanding-smeta-audits', 'best-countries-activewear'],
  },
  turkey: {
    slug: 'turkey',
    name: 'Turkey',
    h1: 'Manufacturers in Turkey',
    title: 'Source from Turkey — Verified Factories Directory',
    intro: 'European-quality knitwear with sub-30-day lead times. Istanbul-based factories dominate premium knits, acid wash, and mercerized cotton.',
    strengths: [
      { cat: 'Streetwear', note: 'Premium knits, mercerized cotton.' },
      { cat: 'Outerwear', note: 'Bomber jackets, technical shells.' },
      { cat: 'Activewear', note: 'European-quality performance basics.' },
    ],
    moqTrend: 'LOW-MEDIUM · 200–3,000 typical',
    leadTimeTrend: '20–35 days',
    commonCerts: ['ISO 9001', 'GOTS', 'OEKO-TEX', 'BCI'],
    relatedGuides: ['source-streetwear-portugal', 'oem-vs-odm'],
  },
  portugal: {
    slug: 'portugal',
    name: 'Portugal',
    h1: 'Manufacturers in Portugal',
    title: 'Source from Portugal — Verified Factories Directory',
    intro: 'The premium European cut-and-sew capital. Porto, Braga, and Guimarães host elevated streetwear, denim, and knit factories serving luxury and contemporary brands.',
    strengths: [
      { cat: 'Streetwear', note: 'Heavyweight fleece, garment dyeing.' },
      { cat: 'Denim', note: 'Selvedge, laser-whiskered, vintage wash.' },
      { cat: 'Outerwear', note: 'Technical shells, sealed seams.' },
    ],
    moqTrend: 'LOW · 100–1,500 typical (boutique)',
    leadTimeTrend: '35–60 days',
    commonCerts: ['GOTS', 'OEKO-TEX', 'GRS', 'BCI'],
    relatedGuides: ['source-streetwear-portugal', 'moq-negotiation-101'],
  },
  pakistan: {
    slug: 'pakistan',
    name: 'Pakistan',
    h1: 'Manufacturers in Pakistan',
    title: 'Source from Pakistan — Verified Factories Directory',
    intro: 'Cotton-heavy supply chain with strong terry, knit, and woven capability. Cost-competitive entry for brands sourcing basics and bath/loungewear.',
    strengths: [
      { cat: 'Streetwear', note: 'Cotton-rich knits, terry, fleece.' },
      { cat: 'Workwear', note: 'Heavy twill, drill, canvas.' },
      { cat: 'Accessories', note: 'Cotton accessories, towels, lounge.' },
    ],
    moqTrend: 'MEDIUM-HIGH · 500–5,000 typical',
    leadTimeTrend: '40–60 days',
    commonCerts: ['WRAP', 'BSCI', 'GOTS', 'OEKO-TEX'],
    relatedGuides: ['identify-legit-factories', 'understanding-smeta-audits'],
  },
};

export const getGuideBySlug = (slug) => GUIDES.find(g => g.slug === slug);
